import React from 'react';

import { Box, Paper, Stack, Theme, Tooltip, Typography, alpha, useTheme } from '@mui/material';

import { FreshnessBucketCount } from './propertyCoverageUtils';

interface Props {
  /** Bucket counts aligned with FRESHNESS_BUCKETS, freshest first. */
  freshness: Array<FreshnessBucketCount>;
  itineraryCount: number;
}

// Green → red gradient, hue-aligned with the calendar's age bands but at full resolution:
// greens for the fresh hours, amber for the 6–48h middle, red for stale. Each background is
// paired with a text colour that stays readable for the in-segment percentage label.
function bucketStyle(theme: Theme, bucketIndex: number): { bgcolor: string; color: string } {
  switch (bucketIndex) {
    case 0:
      return { bgcolor: theme.palette.success.dark, color: theme.palette.common.white };
    case 1:
      return { bgcolor: theme.palette.success.main, color: theme.palette.common.white };
    case 2:
      return { bgcolor: theme.palette.success.light, color: theme.palette.text.primary };
    case 3:
      return { bgcolor: alpha(theme.palette.success.main, 0.35), color: theme.palette.text.primary };
    case 4:
      return { bgcolor: theme.palette.warning.light, color: theme.palette.text.primary };
    case 5:
      return { bgcolor: theme.palette.warning.main, color: theme.palette.text.primary };
    case 6:
      return { bgcolor: alpha(theme.palette.error.main, 0.45), color: theme.palette.text.primary };
    default:
      return { bgcolor: theme.palette.error.main, color: theme.palette.common.white };
  }
}

// Below this share the label cannot fit inside its segment; it is dropped and the tooltip
// (plus the legend underneath) carries the numbers instead. The percentage and count are
// STACKED as two short lines, which is what lets even ~4%-wide segments carry both.
const MIN_PCT_FOR_LABEL = 4;

/**
 * One horizontal stacked bar summarising the price-freshness shape of the whole property:
 * each segment's width is the share of itineraries whose last hint falls in that age bucket,
 * with the percentage rendered inside the segment. A healthy property is a wall of green;
 * a stale one is instantly a wall of red.
 */
export default function FreshnessDistributionBar({ freshness, itineraryCount }: Props) {
  const theme = useTheme();

  return (
    <Paper sx={{ p: 2 }}>
      <Stack direction="row" spacing={1} alignItems="baseline" mb={1.5}>
        <Typography variant="body2" color="text.secondary">
          Price freshness
        </Typography>
        <Typography variant="caption" color="text.secondary">
          how long ago each of the {itineraryCount.toLocaleString()} itineraries was last hinted
        </Typography>
      </Stack>

      <Stack direction="row" sx={{ height: 48, borderRadius: 1, overflow: 'hidden' }}>
        {freshness.map(
          (bucket, i) =>
            bucket.count > 0 && (
              <Tooltip
                key={bucket.label}
                arrow
                title={`${bucket.label}: ${bucket.count.toLocaleString()} itineraries (${bucket.pct}%)`}
              >
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    // Strictly proportional — no per-segment minimum width, which would distort
                    // the "width = share of itineraries" encoding when many sliver buckets exist.
                    // Tiny segments are hard to hover; the legend below carries their numbers.
                    flexGrow: bucket.count,
                    flexBasis: 0,
                    overflow: 'hidden',
                    ...bucketStyle(theme, i),
                  }}
                >
                  {bucket.pct >= MIN_PCT_FOR_LABEL && (
                    <Stack alignItems="center" sx={{ overflow: 'hidden' }}>
                      <Typography
                        variant="caption"
                        // Explicit colour: the theme's caption variant sets its own, which would
                        // override the white inherited from dark segments.
                        sx={{
                          fontWeight: 600,
                          fontSize: 14,
                          lineHeight: 1.3,
                          whiteSpace: 'nowrap',
                          color: bucketStyle(theme, i).color,
                        }}
                      >
                        {bucket.pct}%
                      </Typography>
                      <Typography
                        variant="caption"
                        sx={{
                          fontSize: 12,
                          lineHeight: 1.3,
                          whiteSpace: 'nowrap',
                          color: bucketStyle(theme, i).color,
                        }}
                      >
                        ({bucket.count.toLocaleString()})
                      </Typography>
                    </Stack>
                  )}
                </Box>
              </Tooltip>
            ),
        )}
      </Stack>

      <Stack direction="row" spacing={2} mt={1} flexWrap="wrap" useFlexGap>
        {freshness.map(
          (bucket, i) =>
            bucket.count > 0 && (
              <Stack key={bucket.label} direction="row" spacing={0.75} alignItems="center">
                <Box sx={{ width: 12, height: 12, borderRadius: 0.5, bgcolor: bucketStyle(theme, i).bgcolor }} />
                <Typography variant="caption" color="text.secondary">
                  {bucket.label}: {bucket.pct}% ({bucket.count.toLocaleString()})
                </Typography>
              </Stack>
            ),
        )}
      </Stack>
    </Paper>
  );
}
